import React, { useEffect, useRef, useState } from 'react';
import styles from './index.module.scss';

/** 首屏以下模块：距视口 240px 时再挂载子树 */
export const VIEWPORT_LAZY_ROOT_MARGIN = '240px 0px';

export interface ViewportLazyMountProps {
  id?: string;
  className?: string;
  minHeight?: number | string;
  rootMargin?: string;
  /** 进视口前占位，未传时使用默认灰色块 */
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

const ViewportLazyMount: React.FC<ViewportLazyMountProps> = ({
  id,
  className,
  minHeight = 320,
  rootMargin = VIEWPORT_LAZY_ROOT_MARGIN,
  fallback,
  children,
}) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const [shouldMount, setShouldMount] = useState(false);

  useEffect(() => {
    const element = hostRef.current;
    if (!element) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setShouldMount(true);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setShouldMount(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold: 0.01 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [rootMargin]);

  return (
    <div
      ref={hostRef}
      id={id}
      className={className}
      style={!shouldMount && fallback == null ? { minHeight } : undefined}
    >
      {shouldMount ? children : fallback ?? <div className={styles.placeholder} aria-hidden="true" />}
    </div>
  );
};

export default ViewportLazyMount;
